import { Component } from 'react';

import { theme } from '@prose-ui';
import { ClassNames, legacyTheme, styled } from '@prose-ui/legacy';
import { withStyles } from 'legacyStyles';

import { Image } from 'Components/Image';

import CardProps from 'PropTypes/CardProps';

import { handleKeyPress } from 'utils/a11y';

const styles = (_theme, _props, classes) => ({
  root: {
    background: 'rgba(255, 255, 255, .5)',
    fontFamily: legacyTheme.typography.simplonNorm,
    width: 177,
    minHeight: 77,
    margin: 7,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    borderBottom: '1px solid transparent',
  },

  noImage: {
    // We had to change the cards size for the new question with long texts
    width: 277,
    padding: '10px 18px',
  },
  textRoot: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'var(--align-item-value)',
    justifyContent: 'center',
    margin: '5px 0',
  },
  imgTextRoot: {
    minHeight: 53,
  },
  label: {
    ...legacyTheme.typography.mono1,
    color: theme.colors.neutral['900'],
    textAlign: 'var(--align-text-value)',
    userSelect: 'none',
  },
  noCapsLabel: {
    textTransform: 'none',
  },
  description: {
    marginTop: '7px',
    fontSize: 12,
    lineHeight: legacyTheme.typography.lineHeightNormal,
    letterSpacing: legacyTheme.typography.letterSpacingExtra,
    textAlign: 'var(--align-text-value)',
    color: theme.colors.neutral[800],
  },
  img: {
    width: 157,
    marginBottom: 5,
  },
  feedback: {
    minHeight: 53,
    [legacyTheme.breakpoints.match('sm')]: {
      width: '100%',
      margin: '6px 0',
    },

    border: '1px solid rgba(255, 255, 255, .5)',
    [`& .${classes.label}`]: {
      ...legacyTheme.typography.p2,
      textTransform: 'none',
    },
    [`&.${classes.selectable}`]: {
      borderColor: theme.colors.primary[400],
    },
    [`&.${classes.selected}`]: {
      backgroundColor: theme.colors.primary[400],
      borderColor: theme.colors.primary[400],
      [`& .${classes.label}`]: {
        color: theme.colors.neutral[100],
      },
    },
  },
  consultation: {
    minHeight: 'auto',
    maxWidth: '100%',
    width: 360,
    padding: 0,
    flexDirection: 'row',

    border: '1px solid rgba(255, 255, 255, .5)',
    [`& .${classes.label}`]: {
      ...legacyTheme.typography.p2,
      textTransform: 'none',
    },

    [`&.${classes.noImage}`]: {
      padding: `${legacyTheme.spacing.s16} ${legacyTheme.spacing.s24}`,
    },
    [`& .${classes.imageContainer}`]: {
      height: 126,
      width: 100,
      flex: '0 0 auto',
      backgroundColor: theme.colors.neutral[100],
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    },
    [`&:not(.${classes.noImage}) .${classes.textRoot}`]: {
      alignItems: 'flex-start',
      paddingLeft: legacyTheme.spacing.s24,
      paddingRight: legacyTheme.spacing.s24,
    },

    [`&.${classes.selectable}`]: {
      borderColor: theme.colors.primary[400],
      [`& .${classes.imageContainer}`]: {
        borderRight: `1px solid ${theme.colors.primary[400]}`,
      },
    },
    [`&.${classes.selected}`]: {
      backgroundColor: theme.colors.primary[400],
      borderColor: theme.colors.primary[400],
      [`& .${classes.label}`]: {
        color: theme.colors.neutral[100],
      },
      [`& .${classes.description}`]: {
        color: theme.colors.neutral[600],
      },
    },
  },
  consultationStylingGel: {
    minHeight: 53,
    maxWidth: '100%',
    width: 174,
    height: 53,
    padding: 0,
    flex: '0 0 calc(50% - 20px)',
    '&:last-child': {
      flex: '0 0 calc(100% - 20px)',
    },

    border: '1px solid rgba(255, 255, 255, .5)',
    [`& .${classes.label}`]: {
      ...legacyTheme.typography.p2,
      textTransform: 'none',
    },

    [`&.${classes.noImage}`]: {
      padding: 0,
    },
    [`& .${classes.imageContainer}`]: {
      height: 126,
      width: 100,
      flex: '0 0 auto',
      backgroundColor: theme.colors.neutral[100],
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    },
    [`&:not(.${classes.noImage}) .${classes.textRoot}`]: {
      alignItems: 'flex-start',
      paddingLeft: legacyTheme.spacing.s24,
      paddingRight: legacyTheme.spacing.s24,
    },

    [`&.${classes.selectable}`]: {
      borderColor: theme.colors.primary[400],
      [`& .${classes.imageContainer}`]: {
        borderRight: `1px solid ${theme.colors.primary[400]}`,
      },
    },
    [`&.${classes.selected}`]: {
      backgroundColor: theme.colors.primary[400],
      borderColor: theme.colors.primary[400],
      [`& .${classes.label}`]: {
        color: theme.colors.neutral[100],
      },
      [`& .${classes.description}`]: {
        color: theme.colors.neutral[600],
      },
    },
  },
  imageContainer: {},
  selectable: {},
  selected: {
    background: 'rgba(255, 255, 255, 1)',
    borderBottom: '1px solid black',
  },
  faq: {
    backgroundColor: theme.colors.neutral[100],
    border: '1px solid rgba(0, 0, 0, .5)',
    width: 'initial',
    [`& .${classes.label}`]: {
      ...legacyTheme.typography.p1,
      color: theme.colors.neutral['900'],
      textAlign: 'center',
      userSelect: 'none',
    },
    [`& .${classes.noImage}`]: {
      width: 'initial',
    },
  },
  compactMode: {
    width: 162,
    height: 130,
  },
  chipMode: {
    width: 'auto',
    height: 48,
    padding: '8px 16px !important',
    borderRadius: 10,
    margin: '0px 8px 12px 0px',
  },
  twoColumns: {
    width: 'calc(50% - 14px)',
    height: 70,
  },
});

const IconContainer = styled.div`
  margin-bottom: 16px;
  height: 40px;
  width: 40px;
`;

class Card extends Component {
  renderDescription() {
    const { classes, renderDescription, description } = this.props;

    if (renderDescription) {
      return renderDescription({ className: classes.description, description });
    }
    if (description) {
      return <div className={classes.description}>{description}</div>;
    }
    return null;
  }

  render() {
    const {
      capitalizeLabel,
      classes,
      compactMode,
      chipMode,
      dataClick,
      dataFrom,
      dataTestId,
      imageUrl,
      label,
      icon,
      selectedIcon,
      onClick,
      theme: variant,
      selectable,
      selected,
      showStylingGelLayout,
      twoColumns,
      align,
      className,
    } = this.props;

    return (
      <ClassNames>
        {({ cx }) => (
          <div
            aria-pressed={selected}
            className={cx(
              classes.root,
              { [classes.noImage]: !imageUrl },
              { [classes.feedback]: variant === 'feedback' },
              { [classes.consultation]: variant === 'consultation' && !showStylingGelLayout },
              {
                [classes.consultationStylingGel]:
                  variant === 'consultation' && showStylingGelLayout,
              },
              { [classes.selectable]: selectable },
              { [classes.selected]: selected },
              { [classes.faq]: variant === 'faq' },
              { [classes.compactMode]: compactMode },
              { [classes.chipMode]: chipMode },
              { [classes.twoColumns]: twoColumns },
              className
            )}
            data-click={dataClick}
            data-from={dataFrom}
            data-testid={dataTestId}
            onClick={onClick}
            onKeyPress={handleKeyPress(() => {
              if (window.heap && window.heap.track) {
                window.heap.track('Consultation Enter/Space Hit');
              }
              onClick();
            })}
            role="button"
            tabIndex="0"
          >
            {imageUrl && variant === 'consultation' && (
              <div
                className={classes.imageContainer}
                // TODO: remove condition once placeholders are replaced by real images
                style={{ backgroundImage: `url(${imageUrl?.src ? imageUrl.src : imageUrl})` }}
              />
            )}

            {/*
      The card text consists of label and optionally a description.
      This enables card text to always be vertically centered.
     */}
            <div
              className={cx(classes.textRoot, {
                [classes.imgTextRoot]: imageUrl,
              })}
              style={{
                '--align-item-value': align,
                '--align-text-value': align === 'flex-start' ? 'start' : align,
              }}
            >
              {icon && (
                <IconContainer>
                  <Image
                    alt={`${selected ? 'selected' : ''}${label} icon`}
                    height={40}
                    src={selected ? selectedIcon : icon}
                    width={40}
                  />
                </IconContainer>
              )}
              <div
                className={cx(classes.label, {
                  [classes.noCapsLabel]: !capitalizeLabel,
                })}
              >
                {label}
              </div>
              {this.renderDescription()}
            </div>
          </div>
        )}
      </ClassNames>
    );
  }
}

Card.defaultProps = {
  capitalizeLabel: true,
  dataClick: null,
  dataFrom: null,
  dataTestId: null,
  compactMode: false,
  imageUrl: null,
  icon: null,
  selectedIcon: null,
  renderDescription: null,
  description: null,
  selectable: false,
  theme: 'consultation',
  twoColumns: false,
  align: 'center',
  className: null,
};

Card.propTypes = CardProps;

export default withStyles(Card, styles);
